import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import TopTile from "../../components/HRMS/Dashboard/TopTile";
import Chart from "react-apexcharts";
import { ToastContainer, toast } from "react-toastify";

const AssetDashboard = () => {
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    total_assets: 0,
    active_assets: 0,
    inactive_assets: 0,
    medical_assets: 0,
  });

  const [categoryChart, setCategoryChart] = useState({ labels: [], series: [] });
  const [licenseChart, setLicenseChart] = useState({ labels: [], series: [] });
  const [deptChart, setDeptChart] = useState({ labels: [], series: [] });

  /* ----------------- API CALLS ----------------- */
  const loadSummary = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/asset-dashboard/summary`);
      setSummary(data);
    } catch (err) {
      toast.error("Summary API failed");
      console.error(err);
    }
  };

  const loadCharts = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/asset-dashboard/chart-data`);

      setCategoryChart({
        labels: data.category_split.map((x) => x.label),
        series: data.category_split.map((x) => x.value),
      });

      setLicenseChart({
        labels: data.license_split.map((x) => x.label),
        series: data.license_split.map((x) => x.value),
      });

      setDeptChart({
        labels: data.dept_value_split.map((x) => x.label),
        series: data.dept_value_split.map((x) => x.value),
      });
    } catch (err) {
      toast.error("Chart API failed");
      console.error(err);
    }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([loadSummary(), loadCharts()]);
      setLoading(false);
    })();
  }, []);

  const fmt = (n) => (n ?? 0).toLocaleString();

  /* ----------------- PREMIUM DONUT STYLE (Equal Side Padding) ----------------- */
  const donutOptions = (labels, series = []) => ({
    labels,
    chart: {
      type: "donut",
      height: 340,
      width: "100%",
    },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
        },
      },
    },
    grid: {
      padding: {
        top: 10,
        left: 25,   // Equal spacing
        right: 25,  // Equal spacing
        bottom: 10,
      },
    },
    stroke: {
      width: 2,
      colors: ["#fff"],
    },
    legend: {
      position: "right",
      fontSize: "12px",
      markers: { width: 10, height: 10 },
    },
    colors: [
      "#6C5DD3", "#4EAFE8", "#00C9A7",
      "#FFB547", "#FF6B6B", "#9D44E2",
      "#5EC2B7", "#C8B6FF"
    ],
    dataLabels: { enabled: false },
  });

  /* ----------------- PREMIUM BAR CHART ----------------- */
  const barOptions = {
  chart: {
    type: "bar",
    stacked: true,
    toolbar: { show: false },
  },

  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: "45%",
      borderRadius: 6,
    },
  },

  grid: {
    borderColor: "#E6E9F4",
    strokeDashArray: 4,
  },

  legend: {
    position: "top",
    horizontalAlign: "right",
    markers: { size: 6 },
    itemMargin: { horizontal: 10 },
  },

  tooltip: {
    theme: "dark", // matches screenshot
  },

  xaxis: {
    categories: deptChart.labels,
    labels: { style: { colors: "#6b7280" } },
  },

  yaxis: {
    labels: { style: { colors: "#9ca3af" } },
  },

  colors: [
    "#E5E7EB", // Light grey top (similar to screenshot)
    "#A78BFA", // Light purple
    "#6D28D9"  // Dark purple
  ],
};
// Create stacked bar layers from one dataset
const barSeries = [
  {
    name: "Total Capacity",
    data: deptChart.series.map(v => v + 200), // grey top layer
  },
  {
    name: "Mid Level",
    data: deptChart.series.map(v => Math.floor(v / 2)), // light purple
  },
  {
    name: "Actual Value",
    data: deptChart.series, // dark purple actual value
  },
];


  return (
    <>
      <ToastContainer />

      <div
        className="section-body mt-3"
        style={{
          background:
            "linear-gradient(135deg, #f5f7ff 0%, #ecf3ff 40%, #fefefe 100%)",
          minHeight: "100vh",
        }}
      >
        <div className="container-fluid">

          {/* ======================= METRIC CARDS ======================= */}
          <div className="row g-3 mb-4">
            <TopTile
              label="Total Assets"
              iconClass="icon-layers"
              value={fmt(summary.total_assets)}
              loading={loading}
              color="linear-gradient(90deg,#6C5DD3,#4EAFE8)"
            />
            <TopTile
              label="Active Assets"
              iconClass="icon-check"
              value={fmt(summary.active_assets)}
              loading={loading}
              color="linear-gradient(90deg,#00C9A7,#4EAFE8)"
            />
            <TopTile
              label="Inactive Assets"
              iconClass="icon-close"
              value={fmt(summary.inactive_assets)}
              loading={loading}
              color="linear-gradient(90deg,#FF6B6B,#F67070)"
            />
            <TopTile
              label="Medical Assets"
              iconClass="icon-heart"
              value={fmt(summary.medical_assets)}
              loading={loading}
              color="linear-gradient(90deg,#FFB547,#FFD26F)"
            />
          </div>

          {/* ======================= DONUT ROW ======================= */}
          <div className="row">
            {/* CATEGORY DONUT */}
            <div className="col-lg-6 mb-3">
              <div className="card shadow-lg rounded-3 border-0">
                <div className="card-header bg-transparent border-0">
                  <h6 className="fw-bold" style={{ color: "#1B2559" }}>
                    Category Split
                  </h6>
                </div>

                <div style={{ width: "100%", height: "340px", padding: "14px" }}>
                  <Chart
                    options={donutOptions(categoryChart.labels, categoryChart.series)}
                    series={categoryChart.series}
                    type="donut"
                    height="100%"
                    width="100%"
                  />
                </div>
              </div>
            </div>

            {/* LICENSE DONUT */}
            <div className="col-lg-6 mb-3">
              <div className="card shadow-lg rounded-4 border-0">
                <div className="card-header bg-transparent border-0">
                  <h6 className="fw-bold" style={{ color: "#1B2559" }}>
                    Licensed vs Non Licensed
                  </h6>
                </div>

                <div style={{ width: "100%", height: "340px", padding: "14px" }}>
                  <Chart
                    options={donutOptions(licenseChart.labels, licenseChart.series)}
                    series={licenseChart.series}
                    type="donut"
                    height="100%"
                    width="100%"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ======================= BAR CHART ======================= */}
          <div className="row">
            <div className="col-lg-12 mb-4">
              <div className="card shadow-lg rounded-4 border-0">
                <div className="card-header bg-transparent border-0">
                  <h6 className="fw-bold" style={{ color: "#1B2559" }}>
                    Department Wise Asset Value
                  </h6>
                </div>
                <div className="card-body">
                  <Chart
  options={barOptions}
  series={barSeries}
  type="bar"
  height={350}
/>

                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default AssetDashboard;
